"use client";

import Image from "next/image";
import styles from "./ShortsFeed.module.scss";
import { ShortsFeedItemData } from "./ShortsFeed.types";
import React, { useState, useEffect, useRef, useCallback } from "react";
import ShortsFeedCard from "@components/molecules/ShortsFeedCard/ShortsFeedCard";
import { useKeenSlider, KeenSliderInstance } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { myProfileShortsFeed } from "@services/myProfileService";
import Button from "@components/atoms/Button";
import assetLoader from "@utils/internalImageLoder";
import { useIsMobile } from "@hooks/use-mobile";
import Text from "@components/atoms/Text";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";

function WheelControls(slider: KeenSliderInstance | null) {
  let wheelActive = false;
  let lastScrollTime = 0;
  let isAnimating = false;

  function wheel(e: WheelEvent) {
    if (
      !wheelActive ||
      !slider ||
      !slider.track?.details ||
      isAnimating
    ) return;

    e.preventDefault();
    e.stopPropagation();

    const now = Date.now();
    if (now - lastScrollTime < 1000) return;
    lastScrollTime = now;

    const absDeltaY = Math.abs(e.deltaY);
    const absDeltaX = Math.abs(e.deltaX);

    if (absDeltaY > absDeltaX) {
      isAnimating = true;
      if (e.deltaY > 0) {
        slider.next();
      } else {
        slider.prev();
      }
      
      setTimeout(() => {
        isAnimating = false;
      }, 350);
    }
  }

  slider?.on("created", () => {
    wheelActive = true;
    slider.container?.addEventListener("wheel", wheel, { passive: false });
  });

  slider?.on("destroyed", () => {
    wheelActive = false;
    slider.container?.removeEventListener("wheel", wheel);
  });
}

const ShortsFeed = ((props: any) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const prefetchContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [shortsContentData, setShortsContentData] = useState<ShortsFeedItemData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [prefetchedVideos, setPrefetchedVideos] = useState(new Set());
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const PAGE_SIZE = 10;

  const fetchShortsFeed = async (page: number, isAppend: boolean = false) => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const data = await myProfileShortsFeed(page, PAGE_SIZE);
      if (data && data.length > 0) {
        setShortsContentData(prev => 
          isAppend ? [...prev, ...data] : data
        );
        setCurrentPage(page);
        if (data.length < PAGE_SIZE) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching shorts feed:', error);
      if (!isAppend) {
        setShortsContentData([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchShortsFeed(1, false);
  }, []);

  const getYouTubeVideoId = (url: string): string | null => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const getYouTubeEmbedUrl = (url: string, muted: boolean = true, shouldAutoplay: boolean = false): string | null => {
    const videoId = getYouTubeVideoId(url);
    const muteParam = muted ? '1' : '0';
    const autoplayParam = shouldAutoplay ? '1' : '0';
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=${autoplayParam}&mute=${muteParam}&loop=1&playlist=${videoId}&controls=0&rel=0&modestbranding=1&playsinline=1&showinfo=0&iv_load_policy=3&fs=0&disablekb=1&cc_load_policy=0` : null;
  };

  const prefetchYouTubeVideos = useCallback(() => {
    if (!prefetchContainerRef.current) return;
    
    const container = prefetchContainerRef.current;
    container.innerHTML = '';
    
    const PREFETCH_AHEAD = 1;
    const newPrefetchedSet = new Set();
    
    if (activeItemIndex > 0) {
      for (let i = activeItemIndex + 1; i <= activeItemIndex + PREFETCH_AHEAD && i < shortsContentData.length; i++) {
        const item = shortsContentData[i] as ShortsFeedItemData;
        
        if (item && item?.type === 'Video' && item?.sourceUrl?.includes('youtube.com')) {
          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
              const embedUrl = getYouTubeEmbedUrl(item.sourceUrl, isMuted, false);
              if (embedUrl) {
                const iframe = document.createElement('iframe');
                iframe.src = embedUrl;
                iframe.style.position = 'absolute';
                iframe.style.left = '-9999px';
                iframe.style.width = '1px';
                iframe.style.height = '1px';
                iframe.style.visibility = 'hidden';
                iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
                
                container.appendChild(iframe);
                newPrefetchedSet.add(i);
              }
            }, { timeout: 2000 });
          }
        }
      }
    }
    
    setPrefetchedVideos(newPrefetchedSet);
  }, [activeItemIndex, shortsContentData.length, isMuted]);

  useEffect(() => {
    const totalItems = shortsContentData.length;
    const triggerPoint = totalItems - 2;
    
    if (
      hasMore && 
      !isLoading && 
      totalItems > 0 && 
      activeItemIndex >= triggerPoint
    ) {
      fetchShortsFeed(currentPage + 1, true);
    }
  }, [activeItemIndex, shortsContentData.length, hasMore, isLoading, currentPage])

  useEffect(() => {
    if (slider.current) {
      slider.current.update();
    }
  }, [shortsContentData.length])

  useEffect(() => {
    if (shortsContentData.length > 0) {
      prefetchYouTubeVideos();
    }
  }, [activeItemIndex, shortsContentData.length, isMuted])

  useEffect(() => {
    const ref = containerRef.current;
    if (!ref) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
            window.scrollTo({
              top: ref.getBoundingClientRect().top + window.scrollY - 56,
              behavior: "smooth",
            });
          }
        });
      },
      { threshold: 0.7 }
    );
    observer.observe(ref);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const ref = containerRef.current;
    if (!ref) return;
    
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
            setShowScrollToTop(true);
          } else {
            setIsMuted(true);
            setShowScrollToTop(false);
          }
        });
      },
      { threshold: 0.7 }
    );
    
    observer.observe(ref);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleContainerScroll = (e: WheelEvent) => {
      if (!isMobile) {
        return;
      }

      if (activeItemIndex === 0 && e.deltaY < 0) {
        e.preventDefault();
        e.stopPropagation();
        
        window.scrollBy({
          top: e.deltaY * 2,
          behavior: 'auto'
        });
        return;
      }
      
      if (container.getBoundingClientRect().top <= 100) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    let touchStartY = 0;
    let touchStartTime = 0;
    let isScrollingUp = false;

    const handleTouchStart = (e: TouchEvent) => {
      if (!isMobile) return;
      touchStartY = e.touches[0].clientY;
      touchStartTime = Date.now();
      isScrollingUp = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isMobile) return;
      
      if (activeItemIndex === 0) {
        const touchCurrentY = e.touches[0].clientY;
        const deltaY = touchCurrentY - touchStartY;
        const deltaTime = Date.now() - touchStartTime;
        
        if (deltaY > 30 && deltaTime < 500) {
          isScrollingUp = true;
          e.preventDefault();
          
          const scrollAmount = Math.min(deltaY * 0.5, 100);
          window.scrollBy({
            top: -scrollAmount,
            behavior: 'auto'
          });
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isMobile) return;
      
      setTimeout(() => {
        isScrollingUp = false;
      }, 100);
    };

    container.addEventListener('wheel', handleContainerScroll, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('wheel', handleContainerScroll);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [activeItemIndex, isMobile]);

  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    vertical: true,
    slides: { perView: 1 },
    mode: "snap",
    rubberband: false,
    drag: true,
    dragSpeed: 1,
    defaultAnimation: {
      duration: 300,
      easing: t => t,
    },
    slideChanged(slider) {
      setActiveItemIndex(slider.track.details.rel);
    },
    created(slider) {
      if (slider.container) {
        slider.container.style.scrollSnapType = 'y mandatory';
        slider.container.style.scrollSnapStop = 'always';
      }
    },
  }, [WheelControls]);

  const handleNext = () => {
    slider.current?.next();
  };
  const handlePrev = () => {
    slider.current?.prev();
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
  };

  const handleScrollToTop = async () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.HOME_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_scroll_to_top",
        city_name: props.cityName,
      },
    });
  };
  
  const handleShare = (item: ShortsFeedItemData) => {
    if (navigator.share) {
      navigator
        .share({
          title: item.title,
          text: `Check out this ${item.type} from ACKO Drive: ${item.title}`,
          url: item.sourceUrl,
        })
        .then(() => console.log("Successful share"))
        .catch((error) => console.log("Error sharing", error));
    } else {
      alert("Web Share API is not supported in your browser.");
    }
  };

  const isFirstShort = activeItemIndex === 0;
  const isLastShort = !hasMore && activeItemIndex === shortsContentData.length - 1;

  return (
    <div ref={containerRef} className={styles.shortsFeedContainer}>
      <div ref={prefetchContainerRef} className={styles.prefetchContainer}></div>
      {showScrollToTop && (
          <Button
            onClick={handleScrollToTop} 
            className={styles.scrollToTopButton}
          >
            <Image
              src={`${assetLoader()}/icons/chevron-up-light.svg`}
              alt='Previous Short'
              width={20}
              height={20}
              loading="lazy"
              className={styles.scrollToTopIcon}
            />
            <Text className={styles.scrollToTopText} >Scroll to top</Text>
          </Button>
        )
      }
      
        <div className={styles.shortsFeedWrapper}>
          <div>
            <div className={styles.desktopHeadings}>
              {shortsContentData[activeItemIndex] && shortsContentData[activeItemIndex]?.type == "Article" ? (
                <div className={styles.shortsTitle}>Latest news and reviews<br />from Team ACKO Drive</div>
              ) : (
                <div className={styles.shortsTitle}>ACKO Drive shorts</div>
              )}
              <div className={styles.shortsSubTitle}>
                Continue scrolling to discover more
              </div>
            </div>
          </div>
          <div className={styles.feedContentArea}>
            <div
              ref={sliderRef}
              className={`keen-slider ${styles.shortsFeedScrollContainer}`}
            >
              {shortsContentData.map((item: ShortsFeedItemData, index: number) => (
                <div
                  key={`${item.id}-${index}`}
                  className={`keen-slider__slide ${styles.shortsFeedItemWrapper} ${styles.shortsFeedItemHeight}`}
                >
                  <ShortsFeedCard 
                    item={item} 
                    handleShare={handleShare} 
                    isMuted={isMuted}
                    handleMuteToggle={handleMuteToggle}
                    isActive={index === activeItemIndex}
                    isPrefetched={prefetchedVideos.has(index)}
                    getYouTubeEmbedUrl={getYouTubeEmbedUrl}
                    cityName={props.cityName}
                  />
                </div>
              ))}
              {isLoading && (
                <div className={`keen-slider__slide ${styles.shortsFeedItemWrapper} ${styles.shortsFeedItemHeight}`}>
                  <div className={styles.loadingIndicator}>
                    <div className={styles.loadingText}>Loading...</div>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.navigationControls}>
              <Button
                onClick={handlePrev}
                className={`${styles.navButton} ${styles.navButtonUp} ${
                  isFirstShort ? styles.navButtonDisabled : ""
                }`}
                disabled={isFirstShort}
              >
                <Image
                  src={`${assetLoader()}/icons/Arrow_down.svg`}
                  alt='Previous Short'
                  width={32}
                  height={32}
                  loading="lazy"
                />
              </Button>
              <Button
                onClick={handleNext}
                className={`${styles.navButton} ${styles.navButtonDown} ${
                  isLastShort ? styles.navButtonDisabled : ""
                }`}
                disabled={isLastShort}
              >
                <Image
                  src={`${assetLoader()}/icons/Arrow_down.svg`}
                  alt='Next Short'
                  width={32}
                  height={32}
                  loading="lazy"
                />
              </Button>
            </div>
          </div>
        </div>
    </div>
  );
});

export default ShortsFeed;
